"use client";

import { ReloadIcon } from "@radix-ui/react-icons";
import { useStytchB2BClient } from "@stytch/nextjs/b2b";
import Image from "next/image";
import { useState } from "react";

import Logo from "@/src/components/logo";
import { Button } from "@/src/components/shadcn/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/src/components/shadcn/ui/card";
import { Input } from "@/src/components/shadcn/ui/input";
import { useToast } from "@/src/hooks/use-toast";
import { logger } from "@/src/lib/logging/logger";
import { persistSessionAndRedirect, resolveSameOriginRedirect, takeReturnHost } from "@/src/lib/login-session";
import { isServer } from "@/src/lib/runtime";
import { emailSchema } from "@/src/models/common-schema";

const getLoginRedirectUrl = (redirectPath?: string) => {
  const isLocalhost = !isServer && window.location.hostname === "localhost";
  const baseUrl = isLocalhost ? process.env.NEXT_PUBLIC_APP_URL! : "https://dashboard.valeriehealth.com";

  const url = new URL("/authenticate", baseUrl);

  if (redirectPath) {
    logger.info("Setting redirect path", { redirectPath });

    url.searchParams.set("redirect", redirectPath);
  }

  return url.toString();
};

/** Which email the user was just sent, if any. */
type SentEmail = "magic_link" | "password_reset";

const LoginContainer = ({
  orgId,
  redirectUrl,
  errorMessage,
}: {
  orgId: string;
  redirectUrl?: string;
  errorMessage?: string;
}) => {
  const [sentEmail, setSentEmail] = useState<SentEmail | null>(null);
  const [email, setEmail] = useState("");

  return (
    <div className="flex items-center justify-center min-h-screen">
      {sentEmail ? (
        <EmailSentCard email={email} sentEmail={sentEmail} onBack={() => setSentEmail(null)} />
      ) : (
        <EmailInputCard
          email={email}
          setEmail={setEmail}
          orgId={orgId}
          setSentEmail={setSentEmail}
          redirectUrl={redirectUrl}
          errorMessage={errorMessage}
        />
      )}
    </div>
  );
};

export default LoginContainer;

const EmailInputCard = ({
  email,
  setEmail,
  orgId,
  redirectUrl,
  errorMessage,
  setSentEmail,
}: {
  email: string;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
  orgId: string;
  redirectUrl?: string;
  errorMessage?: string;
  setSentEmail: React.Dispatch<React.SetStateAction<SentEmail | null>>;
}) => {
  const [usePassword, setUsePassword] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [hasBlurred, setHasBlurred] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const validateEmail = (emailValue: string) => {
    const result = emailSchema.safeParse(emailValue);
    const isValid = result.success;

    setIsEmailValid(isValid);

    return isValid;
  };

  const getEmailError = () => {
    if (!hasBlurred || isEmailValid) {
      return null;
    }

    const result = emailSchema.safeParse(email);

    return result.success ? null : result.error.issues[0].message;
  };

  const authenticateUrl = getLoginRedirectUrl(redirectUrl);

  const stytchClient = useStytchB2BClient();
  const { toast } = useToast();

  const handleGoogleLogin = async () => {
    stytchClient.oauth.google.start({
      organization_id: orgId,
      login_redirect_url: authenticateUrl,
    });
  };

  const handleLogin = async (e: React.MouseEvent<HTMLButtonElement> | React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setHasBlurred(true);
    const isValidEmail = validateEmail(email);

    if (!isValidEmail) {
      return;
    }

    setIsLoading(true);

    const loginResp = await stytchClient.magicLinks.email.loginOrSignup({
      email_address: email,
      organization_id: orgId,
      login_redirect_url: authenticateUrl,
    });

    if (loginResp.status_code !== 200) {
      toast({
        title: "Something went wrong.",
        description: "Please try again.",
        variant: "destructive",
      });
    } else {
      setSentEmail("magic_link");
    }

    setIsLoading(false);
  };

  const handlePasswordLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setHasBlurred(true);

    if (!validateEmail(email) || !password) {
      return;
    }

    setIsLoading(true);
    setPasswordError(null);

    try {
      const authResp = await stytchClient.passwords.authenticate({
        organization_id: orgId,
        email_address: email,
        password,
        session_duration_minutes: 60,
      });

      if (!authResp.member_authenticated) {
        logger.warn("Password login requires MFA", { memberId: authResp.member_id });
        setPasswordError("Your organization requires additional verification. Please sign in with an email link.");
        setIsLoading(false);

        return;
      }

      // Return to the alternate host the proxy stashed, if the login started on one.
      const returnHost = takeReturnHost();
      const tenantUrl = returnHost ? new URL(`https://${returnHost}`) : new URL(window.location.origin);

      persistSessionAndRedirect(authResp.session_jwt, resolveSameOriginRedirect(redirectUrl, tenantUrl) ?? tenantUrl);
    } catch (error) {
      logger.warn("Password login failed", { error });
      setPasswordError('Incorrect email or password. If you haven\'t set a password yet, use "Set or reset password".');
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    setHasBlurred(true);

    if (!validateEmail(email)) {
      return;
    }

    setIsLoading(true);

    try {
      await stytchClient.passwords.resetByEmailStart({
        organization_id: orgId,
        email_address: email,
        login_redirect_url: authenticateUrl,
        reset_password_redirect_url: authenticateUrl,
      });

      setSentEmail("password_reset");
    } catch (error) {
      logger.error("Password reset email failed", { error });
      toast({
        title: "Something went wrong.",
        description: "Please try again.",
        variant: "destructive",
      });
    }

    setIsLoading(false);
  };

  return (
    <Card className="w-full max-w-lg">
      <CardHeader className="flex flex-col items-center gap-2">
        <Logo size="large" />
        <CardTitle className="text-2xl">Sign in to Valerie Health</CardTitle>
        {errorMessage && <CardDescription className="text-center text-destructive">{errorMessage}</CardDescription>}
        {passwordError && <CardDescription className="text-center text-destructive">{passwordError}</CardDescription>}
        <CardDescription className="text-muted-foreground">
          {usePassword ? "Please enter your email address and password." : "Please enter your email address."}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col">
        <form id="loginForm" className="flex flex-col gap-3" onSubmit={usePassword ? handlePasswordLogin : handleLogin}>
          <Input
            className="p-6 bg-input"
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => {
              const newValue = e.target.value;

              setEmail(newValue);

              validateEmail(newValue);

              // Clear the blurred state when user starts typing to hide errors
              if (hasBlurred) {
                setHasBlurred(false);
              }
            }}
            onBlur={(e) => {
              setHasBlurred(true);

              validateEmail(e.target.value);
            }}
          />
          {usePassword && (
            <Input
              className="p-6 bg-input"
              type="password"
              autoComplete="current-password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </form>
        {getEmailError() && <span className="text-destructive text-center">{getEmailError()}</span>}
      </CardContent>
      <CardFooter className="flex flex-col gap-6 justify-center w-full">
        <Button
          type="submit"
          className="w-full p-6"
          form="loginForm"
          disabled={isLoading || !isEmailValid || (usePassword && !password)}
        >
          {isLoading ? <ReloadIcon className="animate-spin" /> : "Sign in"}
        </Button>
        <Button className="w-full p-6 flex items-center gap-2" variant="outline" onClick={handleGoogleLogin}>
          <Image src="/google.svg" alt="Google" width={24} height={24} />
          Sign in with Google
        </Button>
        <div className="flex flex-col items-center">
          <Button
            variant="link"
            className="text-muted-foreground"
            onClick={() => {
              setUsePassword(!usePassword);
              setPasswordError(null);
            }}
          >
            {usePassword ? "Email me a sign-in link instead" : "Sign in with a password"}
          </Button>
          {usePassword && (
            <Button variant="link" onClick={handlePasswordReset} disabled={isLoading || !isEmailValid}>
              Set or reset password
            </Button>
          )}
        </div>
      </CardFooter>
    </Card>
  );
};

/** Confirms which email was sent and lets the user go back to send another. */
const EmailSentCard = ({ email, sentEmail, onBack }: { email: string; sentEmail: SentEmail; onBack: () => void }) => {
  return (
    <Card className="w-full max-w-lg">
      <CardHeader className="flex flex-col items-center gap-2">
        <Logo size="large" />
        <CardTitle className="text-2xl">Check your email</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-2">
        <CardDescription className="text-muted-foreground text-center">
          {sentEmail === "password_reset" ? "A link to set your password was sent to " : "An email was sent to "}
          <strong>{email}</strong>.
        </CardDescription>
        <Button variant="link" onClick={onBack}>
          {sentEmail === "password_reset" ? "Back to sign in" : "Send another"}
        </Button>
      </CardContent>
    </Card>
  );
};
